import { Avatar, Box, Drawer, Typography } from '@mui/material'
import { getUserNameFromEmail, getUserNameInitials } from 'utils/chatFunctions'

import ChatRoomsPane from 'components/ChatWindow/ChatRoomsPane'
import ChatSendControl from 'components/ChatWindow/ConversationPane/ChatSendControl'
import ConversationHeader from 'components/ChatWindow/ConversationPane/ConversationHeader'
import ConversationWindow from 'components/ChatWindow/ConversationPane/ConversationWindow'
import { styled } from '@mui/material/styles'
import { useChatData } from 'components/contexts/ChatContext'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.itemBackground,
    width: 'min(1062px, 100vw)',
    maxWidth: '100vw',
    height: 'min(750px, calc(100vh - 32px))',
    top: 'max(16px, calc((100vh - 750px) / 2))',
    overflow: 'hidden',
  },
}))

const ChatDrawer = ({ children, selectorToggle, setSelectorToggle }) => {
  const onCloseDrawer = () => {
    setSelectorToggle(false)
  }

  return (
    <StyledDrawer anchor='right' disableEnforceFocus open={selectorToggle} onClose={onCloseDrawer}>
      {children}
    </StyledDrawer>
  )
}

const getOtherMembers = (members, currentUser) =>
  Array.isArray(members) ? members.filter((member) => member !== currentUser) : []

const ConversationDetails = () => {
  const { chat } = useChatData()
  const { user } = useInnovaAuth()
  const { getTextColor } = useInnovaTheme()
  const members = getOtherMembers(chat.selectedChatRoom?.members, user?.name)

  return (
    <Box sx={{ color: getTextColor(), p: 2 }}>
      <Typography variant='overline' color='text.secondary'>
        Members
      </Typography>
      {members.map((member) => (
        <Box key={member} sx={{ alignItems: 'center', display: 'flex', gap: 1.25, py: 1 }}>
          <Avatar sx={{ height: 32, width: 32, fontSize: 13 }}>{getUserNameInitials(member)}</Avatar>
          <Typography noWrap variant='body2'>
            {getUserNameFromEmail(member)}
          </Typography>
        </Box>
      ))}
      {!members.length && (
        <Typography color='text.secondary' variant='body2' sx={{ mt: 1 }}>
          Select a conversation to see its members.
        </Typography>
      )}
    </Box>
  )
}

const ChatLayout = ({ showDetails }) => {
  const { getActionBarColor, getBackColor, getGrayColor, getTextColor } = useInnovaTheme()
  const paneStyle = {
    bgcolor: getBackColor(),
    border: '1px solid',
    borderColor: getGrayColor(0.08),
    borderRadius: 3,
    boxShadow: '0 12px 30px rgba(0,0,0,.25)',
    color: getTextColor(),
    minHeight: 0,
    overflow: 'hidden',
  }

  return (
    <Box
      sx={{
        bgcolor: getActionBarColor(),
        display: 'grid',
        gap: 1,
        gridTemplateColumns: showDetails ? '400px minmax(0, 1fr) 260px' : '400px minmax(0, 1fr)',
        height: '100%',
        minHeight: 0,
        p: 1,
        '@media (max-width: 800px)': {
          gridTemplateColumns: '325px minmax(420px, 1fr)',
          overflowX: 'auto',
        },
      }}>
      <Box component='aside' sx={{ ...paneStyle, minWidth: 0 }}>
        <ChatRoomsPane />
      </Box>
      <Box component='main' sx={{ ...paneStyle, display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) auto' }}>
        <ConversationHeader />
        <ConversationWindow />
        <Box sx={{ bgcolor: getGrayColor(0.015), borderTop: '1px solid', borderColor: getGrayColor(0.08) }}>
          <ChatSendControl />
        </Box>
      </Box>
      {showDetails && (
        <Box
          component='aside'
          sx={{
            ...paneStyle,
            overflowY: 'auto',
            '@media (max-width: 800px)': { display: 'none' },
          }}>
          <ConversationDetails />
        </Box>
      )}
    </Box>
  )
}

const ChatWindow = ({ open: chatToggle, onClose: setSelectorToggle, showDetails = false }) => {
  return (
    <ChatDrawer selectorToggle={chatToggle} setSelectorToggle={setSelectorToggle}>
      <ChatLayout showDetails={showDetails} />
    </ChatDrawer>
  )
}

export default ChatWindow
